/**
 * HUD. Plain DOM layered over the canvas - no WebGL text, no UI framework.
 *
 * DESIGNED FOR AGES 6-9, which drives every choice here:
 *   - Icons and colors carry the meaning; words are backup, never the only signal.
 *   - Heat is a face that changes expression, not a number. A 7-year-old reads
 *     "granny is getting cross" from a face instantly and from a bar slowly.
 *   - Objective text is under six words.
 *   - Nothing blinks or moves unless it needs attention right now.
 */

import type { HeatTier } from '@/core/events';
import type { ObjectiveProgress } from '@/game/game';
import type { QualityTierName } from '@/render/qualityTiers';

export interface HudState {
  heat: number;
  tier: HeatTier;
  contact: number;
  grannyState: string;
  objectives: ObjectiveProgress[];
  keysCollected: number;
  keysRequired: number;
  chaos: number;
  cozy: number;
  hiding: boolean;
  /** Hidden, but she watched the cat get in - concealment does not apply. */
  hidingSeen: boolean;
  /** 0 = ready, 1 = just used. */
  dashCooldown: number;
  pounceCharge: number;
  carrying: string | null;
  phase: 'playing' | 'caught' | 'won';
  exhausted: boolean;
  debug: {
    fps: number;
    p95: number;
    quality: QualityTierName;
    placeholders: number;
    propCount: number;
    particles: number;
    /** Live count from renderer.info - this is what you re-baseline the budget against. */
    drawCalls: number;
    triangles: number;
    outlines: { character: number; world: number };
  };
}

/** Granny's mood, as a face. The primary heat readout. */
const TIER_FACES: Record<HeatTier, string> = {
  standby: '😊',
  suspicious: '🤔',
  hunting: '😠',
  frenzy: '🤬',
};

const TIER_LABELS: Record<HeatTier, string> = {
  standby: 'Calm',
  suspicious: 'Suspicious',
  hunting: 'Looking for you!',
  frenzy: 'FURIOUS!',
};

export class Hud {
  private readonly heatFace: HTMLElement;
  private readonly heatLabel: HTMLElement;
  private readonly heatFill: HTMLElement;
  private readonly contactFill: HTMLElement;
  private readonly contactWrap: HTMLElement;
  private readonly objectiveList: HTMLElement;
  private readonly keyCount: HTMLElement;
  private readonly scoreChaos: HTMLElement;
  private readonly scoreCozy: HTMLElement;
  private readonly hidingBadge: HTMLElement;
  private readonly carryBadge: HTMLElement;
  private readonly dashPip: HTMLElement;
  private readonly pounceArc: HTMLElement;
  private readonly banner: HTMLElement;
  private readonly debugPanel: HTMLElement;
  private readonly vignette: HTMLElement;

  private debugVisible = false;
  private lastObjectiveSignature = '';

  constructor(root: HTMLElement) {
    root.innerHTML = TEMPLATE;

    this.heatFace = mustFind(root, '#heat-face');
    this.heatLabel = mustFind(root, '#heat-label');
    this.heatFill = mustFind(root, '#heat-fill');
    this.contactFill = mustFind(root, '#contact-fill');
    this.contactWrap = mustFind(root, '#contact-wrap');
    this.objectiveList = mustFind(root, '#objectives');
    this.keyCount = mustFind(root, '#key-count');
    this.scoreChaos = mustFind(root, '#score-chaos');
    this.scoreCozy = mustFind(root, '#score-cozy');
    this.hidingBadge = mustFind(root, '#hiding-badge');
    this.carryBadge = mustFind(root, '#carry-badge');
    this.dashPip = mustFind(root, '#dash-pip');
    this.pounceArc = mustFind(root, '#pounce-arc');
    this.banner = mustFind(root, '#banner');
    this.debugPanel = mustFind(root, '#debug');
    this.vignette = mustFind(root, '#vignette');

    // Backtick toggles the debug panel. Deliberately an obscure key so a kid
    // cannot summon a wall of numbers by accident.
    window.addEventListener('keydown', (event) => {
      if (event.code !== 'Backquote') return;
      this.debugVisible = !this.debugVisible;
      this.debugPanel.classList.toggle('visible', this.debugVisible);
    });
  }

  update(state: HudState): void {
    // --- Heat: face first, bar second ---------------------------------
    this.heatFace.textContent = TIER_FACES[state.tier];
    this.heatLabel.textContent = TIER_LABELS[state.tier];
    this.heatFill.style.width = `${Math.round(state.heat)}%`;
    this.heatFill.dataset.tier = state.tier;
    this.heatFace.classList.toggle('shaking', state.tier === 'frenzy');

    // --- Contact: only shown when it matters -------------------------
    // A permanently visible detection meter teaches kids to watch the meter
    // instead of the world. It appears only once she is actually onto you.
    const showContact = state.contact > 0.02;
    this.contactWrap.classList.toggle('visible', showContact);
    this.contactFill.style.width = `${Math.round(state.contact * 100)}%`;
    this.contactFill.classList.toggle('full', state.contact >= 1);

    // Red edge vignette while spotted - peripheral, wordless, unmissable.
    this.vignette.style.opacity = state.contact >= 1 ? '0.55' : `${state.contact * 0.25}`;

    // --- Objectives ---------------------------------------------------
    const signature = state.objectives
      .map((objective) => `${objective.def.id}:${objective.current}:${objective.complete}`)
      .join('|');
    if (signature !== this.lastObjectiveSignature) {
      this.lastObjectiveSignature = signature;
      this.renderObjectives(state.objectives);
    }

    this.keyCount.textContent = `${state.keysCollected}/${state.keysRequired}`;
    this.scoreChaos.textContent = String(state.chaos);
    this.scoreCozy.textContent = String(state.cozy);

    // Hiding somewhere she watched you climb into does NOT conceal you, and a
    // badge that said "Hidden" either way would be actively teaching the wrong
    // lesson. Icon carries it: peeking hands versus a pair of eyes.
    this.hidingBadge.classList.toggle('visible', state.hiding);
    this.hidingBadge.classList.toggle('seen', state.hidingSeen);
    this.hidingBadge.textContent = state.hidingSeen ? '👀 She saw you' : '🫣 Hidden';
    this.carryBadge.classList.toggle('visible', state.carrying !== null);

    // --- Abilities ----------------------------------------------------
    this.dashPip.classList.toggle('ready', state.dashCooldown <= 0.001);
    this.dashPip.style.setProperty('--fill', `${(1 - state.dashCooldown) * 100}%`);
    this.pounceArc.classList.toggle('visible', state.pounceCharge > 0);
    this.pounceArc.style.setProperty('--charge', `${state.pounceCharge * 100}%`);

    // --- Banner -------------------------------------------------------
    if (state.phase === 'caught') {
      this.showBanner('Caught! Back to bed…', 'caught');
    } else if (state.phase === 'won') {
      // Deliberately nothing. The level card is the win feedback, and a banner
      // sitting behind its dimmed overlay just competes with the stars.
      this.hideBanner();
    } else if (state.exhausted) {
      // Telling the player she is winded turns the wheeze from a random pause
      // into a readable opportunity.
      this.showBanner('She needs a breather!', 'tip');
    } else {
      this.hideBanner();
    }

    if (this.debugVisible) {
      const d = state.debug;
      this.debugPanel.textContent =
        `fps ${d.fps.toFixed(0)}  p95 ${d.p95.toFixed(1)}ms  quality ${d.quality}\n` +
        `draws ${d.drawCalls}  tris ${d.triangles}  ` +
        `outlines ${d.outlines.character}c/${d.outlines.world}w\n` +
        `granny ${state.grannyState}  heat ${state.heat.toFixed(1)} (${state.tier})  ` +
        `contact ${state.contact.toFixed(2)}\n` +
        `props ${d.propCount}  particles ${d.particles}  placeholder assets ${d.placeholders}`;
    }
  }

  private renderObjectives(objectives: ObjectiveProgress[]): void {
    this.objectiveList.innerHTML = '';
    for (const objective of objectives) {
      const row = document.createElement('li');
      row.className = objective.complete ? 'done' : '';

      const check = document.createElement('span');
      check.className = 'check';
      check.textContent = objective.complete ? '✓' : (ICONS[objective.def.kind] ?? '•');

      const label = document.createElement('span');
      label.className = 'label';
      label.textContent = objective.def.label;

      const count = document.createElement('span');
      count.className = 'count';
      // Hide the counter on single-step objectives - "1/1" is noise.
      count.textContent =
        objective.def.target > 1 ? `${objective.current}/${objective.def.target}` : '';

      row.append(check, label, count);
      this.objectiveList.append(row);
    }
  }

  private showBanner(text: string, kind: string): void {
    this.banner.textContent = text;
    this.banner.dataset.kind = kind;
    this.banner.classList.add('visible');
  }

  private hideBanner(): void {
    this.banner.classList.remove('visible');
  }
}

const ICONS: Record<string, string> = {
  mischief: '💥',
  heist: '🎒',
  discovery: '🔍',
};

function mustFind(root: HTMLElement, selector: string): HTMLElement {
  const element = root.querySelector<HTMLElement>(selector);
  if (!element) throw new Error(`HUD template is missing ${selector}`);
  return element;
}

const TEMPLATE = `
  <div id="vignette"></div>

  <div class="hud-top-left">
    <div class="heat-panel">
      <div id="heat-face">😊</div>
      <div class="heat-meta">
        <div id="heat-label">Calm</div>
        <div class="heat-bar"><div id="heat-fill" data-tier="standby"></div></div>
      </div>
    </div>
    <div id="contact-wrap" class="contact-bar">
      <span class="eye">👁</span>
      <div class="contact-track"><div id="contact-fill"></div></div>
    </div>
  </div>

  <div class="hud-top-right">
    <div class="stat"><span class="stat-icon">🔑</span><span id="key-count">0/2</span></div>
    <div class="stat"><span class="stat-icon">💥</span><span id="score-chaos">0</span></div>
    <div class="stat"><span class="stat-icon">😻</span><span id="score-cozy">0</span></div>
  </div>

  <ul id="objectives"></ul>

  <div class="hud-bottom-left">
    <div id="hiding-badge" class="badge">🫣 Hidden</div>
    <div id="carry-badge" class="badge">🎒 Carrying</div>
  </div>

  <div class="hud-bottom-right">
    <div id="dash-pip" class="pip ready">⚡</div>
    <div id="pounce-arc" class="pip">🐾</div>
  </div>

  <div id="banner"></div>
  <pre id="debug"></pre>
`;
